import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme.js';
import { useDashboard } from '../lib/queries.js';
import type { DashboardData, DueEquipment } from '../lib/queries.js';
import { assetTypeIcon } from '../lib/asset-types.js';
import { Icon } from './Icon.js';
import { StatusPill } from './StatusPill.js';

const PREVIEW = 5;

/**
 * Admin dashboard: what needs attention across the org — overdue checks,
 * open faults (from the fault register) and
 * equipment expiring or due for service. Hidden when everything is clear.
 */
export function AdminOverview({ orgId }: { orgId: string }) {
  const { data, isLoading } = useDashboard(orgId, true);
  if (isLoading || !data) return null;

  const due = mergeDue(data);
  const allClear = !data.overdueRuns.length && !data.openFaults.length && !due.length;

  return (
    <div style={{ borderBottom: `1px solid ${theme.border}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, padding: '12px 16px' }}>
        <Tile label="Overdue checks" value={data.overdueRuns.length} tone="danger" target="overdue" />
        <Tile label="Open faults" value={data.openFaults.length} tone="danger" target="issues" />
        <Tile label="Due soon" value={due.length} tone="warn" target="due" />
      </div>

      {allClear && (
        <div style={{ padding: '0 16px 12px', fontSize: 13, color: theme.textMuted }}>
          Nothing needs attention — no overdue checks, open faults or equipment due in the next {data.alertWindowDays} days.
        </div>
      )}

      <OverdueList runs={data.overdueRuns} />
      <FaultsList faults={data.openFaults} />
      <DueList items={due} windowDays={data.alertWindowDays} today={data.today} />
    </div>
  );
}

function Tile({ label, value, tone, target }: {
  label: string; value: number; tone: 'danger' | 'warn'; target: string;
}) {
  const t = value > 0 ? theme.status[tone] : theme.status.ok;
  return (
    <a href={`#${target}`} onClick={(e) => {
      e.preventDefault();
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }} style={{
      display: 'block', padding: '10px 12px', borderRadius: 12, textDecoration: 'none',
      background: t.bg, color: t.fg, pointerEvents: value ? 'auto' : 'none',
    }}>
      <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{label}</div>
    </a>
  );
}

function OverdueList({ runs }: { runs: DashboardData['overdueRuns'] }) {
  const nav = useNavigate();
  const [all, setAll] = useState(false);
  if (!runs.length) return null;
  return (
    <Section id="overdue" title="Overdue checks" count={runs.length} all={all} onAll={() => setAll(true)}>
      {(all ? runs : runs.slice(0, PREVIEW)).map((r) => (
        <Row key={r.id} icon={assetTypeIcon(r.assetType)} onClick={() => nav(`/checks/${r.id}`)}
          title={r.assetName}
          subtitle={`${r.teamName ?? 'Any crew'} · was due ${fmtDate(r.periodEnd)} · ${r.checkedCount}/${r.itemCount} checked`} />
      ))}
    </Section>
  );
}

function FaultsList({ faults }: { faults: DashboardData['openFaults'] }) {
  const nav = useNavigate();
  const [all, setAll] = useState(false);
  if (!faults.length) return null;
  return (
    <Section id="issues" title="Open faults" count={faults.length} all={all} onAll={() => setAll(true)} more={() => nav('/faults')}>
      {(all ? faults : faults.slice(0, PREVIEW)).map((f) => (
        <Row key={f.id} icon={assetTypeIcon(f.assetType)}
          onClick={() => nav(`/faults/${f.id}`)}
          title={f.title}
          subtitle={[`${f.assetName}${f.sectionName ? ` › ${f.sectionName}` : ''}`, `since ${fmtDate(f.reportedAt)}`,
            f.assignedTo ? `→ ${f.assignedTo.displayName}` : 'unassigned', f.externalRef && `Ref ${f.externalRef}`]
            .filter(Boolean).join(' · ')}
          right={<>
            {f.offRun && <Icon name="warning" size={14} style={{ color: theme.status.danger.dot }} />}
            <StatusPill status={f.status} size="sm" />
          </>} />
      ))}
    </Section>
  );
}

type DueRow = DueEquipment & { kind: 'Expires' | 'Service due' };

function DueList({ items, windowDays, today }: { items: DueRow[]; windowDays: number; today: string }) {
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  return (
    <Section id="due" title={`Due in the next ${windowDays} days`} count={items.length} all={all} onAll={() => setAll(true)}>
      {(all ? items : items.slice(0, PREVIEW)).map((e) => {
        const overdue = e.dueDate.slice(0, 10) < today;
        return (
          <Row key={`${e.kind}-${e.id}`} icon={e.equipmentType === 'tracked' ? 'package' : 'kit'}
            title={`${e.name}${e.serialNumber ? ` #${e.serialNumber}` : ''}`}
            subtitle={[e.location ?? 'Not placed anywhere', e.categoryName].filter(Boolean).join(' · ')}
            right={
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                background: overdue ? theme.status.danger.bg : theme.status.warn.bg,
                color: overdue ? theme.status.danger.fg : theme.status.warn.fg,
              }}>
                {overdue ? `${e.kind === 'Expires' ? 'Expired' : 'Service overdue'}` : e.kind} {fmtDate(e.dueDate)}
              </span>
            } />
        );
      })}
    </Section>
  );
}

function mergeDue(d: DashboardData): DueRow[] {
  return [
    ...d.expiring.map((e) => ({ ...e, kind: 'Expires' as const })),
    ...d.serviceDue.map((e) => ({ ...e, kind: 'Service due' as const })),
  ].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

// ── Layout bits ───────────────────────────────────────────────

function Section({ id, title, count, all, onAll, more, children }: {
  id: string; title: string; count: number; all: boolean; onAll: () => void; more?: () => void; children: React.ReactNode;
}) {
  return (
    <section id={id} style={{ scrollMarginTop: 80 }}>
      <div style={{
        fontSize: 13, fontWeight: 600, color: theme.textDim, padding: '12px 20px 6px',
        borderTop: `1px solid ${theme.border}`, background: theme.bg,
      }}>
        {title} <span style={{ color: theme.textMuted, fontWeight: 500 }}>({count})</span>
        {more && (
          <button onClick={more} style={{ float: 'right', background: 'none', border: 'none', padding: 0, color: theme.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font }}>
            Register →
          </button>
        )}
      </div>
      {children}
      {!all && count > PREVIEW && (
        <button onClick={onAll} style={{
          width: '100%', padding: '10px 0', background: 'none', border: 'none',
          borderTop: `1px solid ${theme.border}`, color: theme.accent, fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: theme.font,
        }}>
          Show all {count}
        </button>
      )}
    </section>
  );
}

function Row({ icon, title, subtitle, right, onClick }: {
  icon: string; title: string; subtitle: string; right?: React.ReactNode; onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
      padding: '10px 20px', background: theme.surface, border: 'none',
      borderTop: `1px solid ${theme.border}`, cursor: onClick ? 'pointer' : 'default', fontFamily: theme.font,
    }}>
      <Icon name={icon} size={18} style={{ color: theme.textMuted, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{title}</div>
        <div style={{
          fontSize: 12, color: theme.textMuted, marginTop: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{subtitle}</div>
      </div>
      {right && <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>{right}</div>}
    </Tag>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
